import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CaretRight, House } from '@phosphor-icons/react';
import { SchemaScript } from './SchemaMarkup';

const SITE_ORIGIN = 'https://ihgind.com';

/**
 * Breadcrumb - Navigation breadcrumb component
 * Shows: Home > Parent > Current Page
 */
export const Breadcrumb = ({ 
  items = [], // Array of { label, href }
  currentPage,
  className = "",
  variant = "light" // light | dark
}) => {
  const location = useLocation();
  
  // Auto-generate breadcrumbs from URL if items not provided
  const generateBreadcrumbs = () => {
    if (items.length > 0) return items;
    
    const pathParts = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [];
    
    let currentPath = '';
    pathParts.forEach((part, index) => {
      currentPath += `/${part}`;
      const isLast = index === pathParts.length - 1;
      
      // Format the label
      let label = part
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      if (!isLast) {
        breadcrumbs.push({ label, href: currentPath });
      }
    });
    
    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();
  const textColor = variant === 'dark' ? 'text-white/60 hover:text-white' : 'text-concrete hover:text-obsidian';
  const currentColor = variant === 'dark' ? 'text-white' : 'text-obsidian';
  const iconColor = variant === 'dark' ? 'text-white/40' : 'text-concrete/50';

  // BreadcrumbList JSON-LD for SEO. Position 1 is always Home (we
  // strip any caller-supplied Home item so we don't double it up);
  // then the intermediate crumbs; then the current page (no URL).
  const intermediate = breadcrumbs.filter((b) => {
    const href = (b.href || '').replace(/\/+$/, '');
    const label = (b.label || '').trim().toLowerCase();
    return href !== '' && href !== '/' && label !== 'home';
  });
  const listItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
    ...intermediate.map((b, i) => ({
      '@type': 'ListItem',
      position: i + 2,
      name: b.label,
      item: b.href ? `${SITE_ORIGIN}${b.href}` : undefined,
    })),
    ...(currentPage
      ? [{ '@type': 'ListItem', position: intermediate.length + 2, name: currentPage }]
      : []),
  ];
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: listItems,
  };

  return (
    <>
      <SchemaScript schema={breadcrumbSchema} />
      <nav 
        className={`flex items-center gap-2 text-xs ${className}`}
        aria-label="Breadcrumb"
        data-testid="breadcrumb"
      >
        <Link 
          to="/" 
          className={`flex items-center gap-1 ${textColor} transition-colors`}
          aria-label="Home"
        >
          <House weight="fill" className="w-3.5 h-3.5" />
          <span className="sr-only">Home</span>
        </Link>
        
        {breadcrumbs.map((item, index) => (
          <React.Fragment key={`${item.label}-${index}`}>
            <CaretRight weight="bold" className={`w-3 h-3 ${iconColor}`} />
            <Link 
              to={item.href} 
              className={`${textColor} transition-colors uppercase tracking-wide`}
            >
              {item.label}
            </Link>
          </React.Fragment>
        ))}
        
        {currentPage && (
          <>
            <CaretRight weight="bold" className={`w-3 h-3 ${iconColor}`} />
            <span className={`${currentColor} uppercase tracking-wide font-medium`}>
              {currentPage}
            </span>
          </>
        )}
      </nav>
    </>
  );
};

export default Breadcrumb;
